/**
 * Utility for safe Supabase Realtime channel management.
 * Ensures channels are properly unsubscribed and removed to prevent connection leaks.
 */

import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel } from "@supabase/supabase-js";

/**
 * Safely removes a Supabase Realtime channel.
 * Always calls unsubscribe() before removeChannel() to ensure proper cleanup.
 */
export function safeRemoveChannel(channel: RealtimeChannel | null | undefined): void {
  if (!channel) return;
  
  try {
    channel.unsubscribe();
    supabase.removeChannel(channel);
  } catch (error) {
    // Log but don't throw - cleanup should never break the app
    console.warn("[safeRemoveChannel] Error during cleanup:", error);
  }
}

/**
 * Gets the current count of active Supabase channels.
 * Useful for debugging connection issues.
 */
export function getActiveChannelCount(): number {
  try {
    return supabase.getChannels().length;
  } catch {
    return 0;
  }
}

/**
 * Logs all active channels for debugging purposes.
 */
export function logActiveChannels(): void {
  try {
    const channels = supabase.getChannels();
    console.log(`[CHANNELS] Active channels (${channels.length}):`, 
      channels.map(c => c.topic)
    );
  } catch (error) {
    console.warn("[logActiveChannels] Error:", error);
  }
}
